import React, { useState, useEffect } from 'react';
import { LifeBuoy, Plus, Send, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import AttachmentPicker, { filesFromPaste, filterFiles } from '../components/AttachmentPicker';
import AttachmentList from '../components/AttachmentList';
import { supportApi, apiErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { format } from 'date-fns';

const initials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };
const STATUS_CLS = {
  open: 'bg-amber-500/15 text-amber-600',
  in_progress: 'bg-blue-500/15 text-blue-500',
  resolved: 'bg-green-500/15 text-green-600',
};

const SupportPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [newConsent, setNewConsent] = useState(false);
  const [reply, setReply] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [replyFiles, setReplyFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await supportApi.listTickets();
      setTickets(res.data || []);
    } catch (e) {
      toast.error('Could not load your tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTickets(); }, []);

  const openTicket = async (id) => {
    try {
      const res = await supportApi.getTicket(id);
      setSelected(res.data);
      // getTicket marks it read server-side — tell the sidebar to refresh its badge.
      window.dispatchEvent(new Event('support:refresh-unread'));
    } catch (e) {
      toast.error('Could not open that ticket');
    }
  };

  const createTicket = async () => {
    if (!subject.trim() || (!body.trim() && newFiles.length === 0)) return;
    setBusy(true);
    try {
      const res = await supportApi.createTicket(subject.trim(), body.trim(), newConsent, newFiles);
      setSubject(''); setBody(''); setNewConsent(false); setNewFiles([]); setShowNew(false);
      setSelected(res.data);
      loadTickets();
      toast.success('Ticket raised');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not raise the ticket'));
    } finally {
      setBusy(false);
    }
  };

  const toggleConsent = async () => {
    if (!selected) return;
    try {
      const res = await supportApi.setConsent(selected.id, !selected.access_consent);
      setSelected(res.data);
      toast.success(res.data.access_consent ? 'Support access granted' : 'Support access turned off');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not update access'));
    }
  };

  const sendReply = async () => {
    if ((!reply.trim() && replyFiles.length === 0) || !selected) return;
    setBusy(true);
    try {
      const res = await supportApi.reply(selected.id, reply.trim(), replyFiles);
      setReply(''); setReplyFiles([]);
      setSelected(res.data);
      loadTickets();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not send your reply'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-primary" />
              <h1 className="font-heading text-xl font-bold">Support</h1>
            </div>
            {!selected && (
              <Button size="sm" onClick={() => setShowNew((v) => !v)}>
                <Plus className="w-4 h-4 mr-2" /> New ticket
              </Button>
            )}
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 py-6">
          {selected ? (
            <div>
              <button
                onClick={() => setSelected(null)}
                className="text-sm text-muted-foreground flex items-center gap-1 mb-4 hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" /> Back to tickets
              </button>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <h2 className="font-heading font-bold text-lg">{selected.subject}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[selected.status] || ''}`}>
                  {STATUS_LABEL[selected.status] || selected.status}
                </span>
              </div>

              {user?.id === selected.opened_by_user_id && (
                <div className="card-hive p-3 mb-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Allow HolidayHive support to access my account</p>
                    <p className="text-xs text-muted-foreground">
                      Lets support log in as you to help with this ticket. You can turn it off anytime.
                    </p>
                  </div>
                  <button
                    onClick={toggleConsent}
                    className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition ${
                      selected.access_consent
                        ? 'bg-green-500/15 text-green-600 border-green-500/40'
                        : 'border-border text-muted-foreground hover:bg-secondary/40'
                    }`}
                  >
                    {selected.access_consent ? 'Access on' : 'Access off'}
                  </button>
                </div>
              )}

              <div className="space-y-3 mb-4">
                {(selected.messages || []).map((m) => (
                  <div key={m.id} className={`p-3 rounded-lg ${m.from_staff ? 'bg-primary/10' : 'bg-secondary/40'}`}>
                    {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                    <AttachmentList attachments={m.attachments} fetchBlob={supportApi.getAttachment} />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {m.from_staff ? 'HolidayHive Support' : (m.author_name || 'You')}
                      {m.created_at ? ` · ${format(new Date(m.created_at), 'd MMM yyyy, HH:mm')}` : ''}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onPaste={(e) => {
                      const pasted = filesFromPaste(e);
                      if (!pasted.length) return;
                      const accepted = filterFiles(pasted, replyFiles);
                      if (accepted.length) setReplyFiles([...replyFiles, ...accepted]);
                    }}
                    rows={5}
                    placeholder="Write a reply… (paste a screenshot to attach it)"
                    className="input-hive w-full resize-y text-sm min-h-[120px]"
                  />
                  <AttachmentPicker files={replyFiles} setFiles={setReplyFiles} disabled={busy} />
                </div>
                <Button size="sm" onClick={sendReply} disabled={busy || (!reply.trim() && replyFiles.length === 0)}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              {showNew && (
                <div className="card-hive p-4 mb-6">
                  <h2 className="font-heading font-bold mb-3">Raise a ticket</h2>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Subject"
                    className="input-hive mb-2"
                  />
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onPaste={(e) => {
                      const pasted = filesFromPaste(e);
                      if (!pasted.length) return;
                      const accepted = filterFiles(pasted, newFiles);
                      if (accepted.length) setNewFiles([...newFiles, ...accepted]);
                    }}
                    rows={8}
                    placeholder="Describe the issue… (paste a screenshot to attach it)"
                    className="input-hive w-full resize-y text-sm mb-2 min-h-[180px]"
                  />
                  <AttachmentPicker files={newFiles} setFiles={setNewFiles} disabled={busy} />
                  <label className="flex items-start gap-2 mb-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newConsent}
                      onChange={(e) => setNewConsent(e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-muted-foreground">
                      If you think a support engineer could solve this faster by accessing your account,
                      tick here to allow it for this ticket. You can turn it off at any time.
                    </span>
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setShowNew(false); setNewFiles([]); }}>Cancel</Button>
                    <Button size="sm" onClick={createTicket} disabled={busy || !subject.trim() || (!body.trim() && newFiles.length === 0)}>
                      Submit
                    </Button>
                  </div>
                </div>
              )}

              {loading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : tickets.length === 0 ? (
                <div className="text-center py-12">
                  <LifeBuoy className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No tickets yet. Raise one and we'll help.</p>
                </div>
              ) : (
                (() => {
                  const sortDesc = (arr) =>
                    [...arr].sort((a, b) =>
                      new Date(b.last_message_at || b.updated_at) - new Date(a.last_message_at || a.updated_at));
                  const renderRow = (t) => (
                    <button
                      key={t.id}
                      onClick={() => openTicket(t.id)}
                      className="w-full text-left card-hive p-3 hover:bg-secondary/30 transition flex items-center gap-3"
                    >
                      {t.opened_by_avatar ? (
                        <img src={t.opened_by_avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                          {initials(t.opened_by_name)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{t.subject}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {t.opened_by_name || 'Unknown'} · {t.message_count} message{t.message_count !== 1 ? 's' : ''}
                          {t.last_message_at ? ` · ${format(new Date(t.last_message_at), 'd MMM yyyy')}` : ''}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_CLS[t.status] || ''}`}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                    </button>
                  );

                  if (!isAdmin) {
                    return <div className="space-y-2">{sortDesc(tickets).map(renderRow)}</div>;
                  }
                  const mine = sortDesc(tickets.filter((t) => t.mine));
                  const team = sortDesc(tickets.filter((t) => !t.mine));
                  return (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">
                          Your tickets ({mine.length})
                        </h3>
                        {mine.length ? (
                          <div className="space-y-2">{mine.map(renderRow)}</div>
                        ) : (
                          <p className="text-sm text-muted-foreground">You haven't raised any yet.</p>
                        )}
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">
                          Your team's tickets ({team.length})
                        </h3>
                        {team.length ? (
                          <div className="space-y-2">{team.map(renderRow)}</div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No tickets from your team.</p>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SupportPage;
